/**
 * App Settings Template
 *
 * Account-level preferences page: Appearance (light/dark) + Language. Wears the
 * same app shell (sidebar + sticky Header) as the Profile / Billing pages, and
 * reuses `MyProfilePageFacade` purely for that shell wiring (header, sidebar,
 * user menu). The language switcher is injected by the host (web owns i18n).
 *
 * @package @ideoz/ui
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  selectTheme,
  setTheme,
  useAppDispatch,
  useAppSelector,
  type MyProfilePageFacade,
} from '@ideoz/core';
import { cn } from '../../lib/utils';
import { Button } from '../atoms/button';
import { AppLayout } from '../layouts/AppLayout';
import { Header } from '../organisms/Header';

export interface AppSettingsTemplateProps {
  facade: MyProfilePageFacade;
  isWeb: boolean;
  /** Language switcher injected by the host (web owns i18n). */
  languageSwitcher?: React.ReactNode;
}

export default function AppSettingsTemplate({
  facade,
  isWeb,
  languageSwitcher,
}: AppSettingsTemplateProps): JSX.Element {
  const { t } = useTranslation();

  const {
    state: { user },
    header: {
      headerProps,
      userMenuItems,
      userMenuSocialLinks,
      onRequestUpgrade,
      isRequestingUpgrade,
    },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    onRenewSubscription,
  } = facade;

  // Appearance — persisted via redux-persist; the root <html> class is synced in
  // web/src/App.tsx so Tailwind `dark:` variants activate everywhere.
  const dispatch = useAppDispatch();
  const theme = useAppSelector(selectTheme);
  const isDark = theme === 'dark';
  const setMode = useCallback(
    (mode: 'light' | 'dark') => {
      dispatch(setTheme(mode));
    },
    [dispatch]
  );

  return (
    <AppLayout
      currentPath="/app-settings"
      sidebar={facade.sidebar}
      user={{
        isLoggedin: !!user,
        name: user?.name,
        avatar: user?.avatar,
      }}
      userMenuProps={
        user
          ? {
              points: headerProps.points,
              pointsLabel: t('profile.pointsLabel'),
              menuItems: userMenuItems || [],
              socialLinks: userMenuSocialLinks || [],
              onRequestUpgrade,
              isRequestingUpgrade: isRequestingUpgrade || false,
              canRequestUpgrade: (headerProps.points || 0) <= 0,
              isWeb,
            }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? { isVisible: true, daysUntilExpiration, onRenew: onRenewSubscription }
          : undefined
      }
      isWeb={isWeb}
      testId="app-settings-sidemenu"
      className="data-testid-app-settings-page"
      height="screen"
      defaultSidebarCollapsed={false}
    >
      <Header
        {...headerProps}
        showBackButton
        showBackArrow={false}
        backText={t('appSettings.title')}
        isHasBorder
        className="sticky top-0 w-full"
        isWeb
      />

      <section className="mx-auto w-full max-w-5xl px-6 py-10" data-testid="app-settings-page">
        <header className="mb-6 flex flex-col gap-1">
          <h2 className="text-2xl text-start font-semibold text-gray-800 dark:text-gray-100">
            {t('appSettings.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">{t('appSettings.subtitle')}</p>
        </header>

        <div className="flex flex-col gap-8 rounded-2xl border border-gray-100 bg-background p-8 dark:border-gray-700 dark:bg-gray-800">
          {/* Appearance / Theme — mirrors the Language switcher: a label +
              description over two toggle buttons (Light / Dark). */}
          <div className="space-y-2" data-testid="app-settings-appearance-section">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('profile.appearance')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('appSettings.appearanceDescription')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={!isDark ? 'primary' : 'secondary-gray'}
                size="sm"
                onClick={() => setMode('light')}
                aria-pressed={!isDark}
                className={cn('min-w-24', !isDark && 'pointer-events-none')}
                data-testid="appearance-option-light"
              >
                {t('appSettings.lightMode')}
              </Button>
              <Button
                type="button"
                variant={isDark ? 'primary' : 'secondary-gray'}
                size="sm"
                onClick={() => setMode('dark')}
                aria-pressed={isDark}
                className={cn('min-w-24', isDark && 'pointer-events-none')}
                data-testid="appearance-option-dark"
              >
                {t('appSettings.darkMode')}
              </Button>
            </div>
          </div>

          {/* Language (web-only; host injects the switcher) */}
          {languageSwitcher && (
            <div
              className="border-t border-gray-200 pt-8 dark:border-gray-700"
              data-testid="app-settings-language-section"
            >
              {languageSwitcher}
            </div>
          )}
        </div>
      </section>
    </AppLayout>
  );
}
